import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  Refresh,
  ExpandMore,
  Description,
  EventNote,
  Assessment,
  TextSnippet,
  AttachMoney,
  CalendarToday,
  AccountBalance,
  Rule,
  Folder,
  Visibility,
  Download,
  Search,
  CheckCircle,
  Warning,
  Close,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { DocumentLibraryData, DocumentInfo } from '../../types/widget';

interface DocumentLibraryWidgetProps {
  widgetId: string;
  title: string;
}

const getIconComponent = (iconName: string) => {
  const icons: Record<string, React.ReactNode> = {
    description: <Description />,
    event_note: <EventNote />,
    assessment: <Assessment />,
    text_snippet: <TextSnippet />,
    attach_money: <AttachMoney />,
    calendar_today: <CalendarToday />,
    account_balance: <AccountBalance />,
    rule: <Rule />,
    folder: <Folder />,
  };
  return icons[iconName] || <Folder />;
};

const DocumentLibraryWidget: React.FC<DocumentLibraryWidgetProps> = ({ title }) => {
  const [data, setData] = useState<DocumentLibraryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTypes, setExpandedTypes] = useState<string[]>(['script', 'call_sheet', 'dpr']);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getDocumentLibraryData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch document library');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExpand = (type: string) => {
    setExpandedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handlePreview = async (doc: DocumentInfo) => {
    setPreviewLoading(true);
    setPreviewName(doc.name);
    setPreviewOpen(true);
    
    try {
      const response = await apiService.getDocumentSignedUrl(doc.gcs_uri);
      if (response.success && response.data?.signed_url) {
        setPreviewUrl(response.data.signed_url);
      } else {
        throw new Error(response.error || 'Failed to get document URL');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (doc: DocumentInfo) => {
    try {
      const response = await apiService.getDocumentSignedUrl(doc.gcs_uri);
      if (response.success && response.data?.signed_url) {
        const link = document.createElement('a');
        link.href = response.data.signed_url;
        link.download = doc.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewName('');
  };

  const filterDocuments = (docs: DocumentInfo[]): DocumentInfo[] => {
    if (!searchTerm) return docs;
    const term = searchTerm.toLowerCase();
    return docs.filter((doc) => doc.name.toLowerCase().includes(term));
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No document library data available.</Alert>;
  }

  const typeOrder = ['script', 'call_sheet', 'dpr', 'dsr', 'hot_cost', 'schedule', 'budget', 'assumptions', 'other'];

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A202C',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #4A5568',
          backgroundColor: '#2D3748',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Folder sx={{ color: '#4299E1' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip
            label={`${data.total_documents} Documents`}
            size="small"
            sx={{ backgroundColor: '#4A5568' }}
          />
          {data.overall_daily_completion.percent < 100 ? (
            <Chip
              icon={<Warning sx={{ color: '#ECC94B !important', fontSize: 16 }} />}
              label={`${data.overall_daily_completion.percent}% Daily Docs`}
              size="small"
              sx={{
                backgroundColor: 'rgba(236, 201, 75, 0.2)',
                color: '#ECC94B',
              }}
            />
          ) : (
            <Chip
              icon={<CheckCircle sx={{ color: '#48BB78 !important', fontSize: 16 }} />}
              label="All Daily Docs Received"
              size="small"
              sx={{
                backgroundColor: 'rgba(72, 187, 120, 0.2)',
                color: '#48BB78',
              }}
            />
          )}
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#A0AEC0', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: '#1A202C',
              '& input': { color: 'white' },
            },
          }}
        />
      </Box>

      {/* Document Types */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        {typeOrder.map((typeKey) => {
          const typeSummary = data.documents_by_type[typeKey];
          if (!typeSummary) return null;

          const filteredDocs = filterDocuments(typeSummary.documents);
          const hasMissing =
            typeSummary.has_daily_expectation &&
            (typeSummary.days_received || 0) < typeSummary.expected;
          const missingCount = typeSummary.has_daily_expectation
            ? typeSummary.expected - (typeSummary.days_received || 0)
            : 0;

          if (searchTerm && filteredDocs.length === 0) return null;

          return (
            <Accordion
              key={typeKey}
              expanded={expandedTypes.includes(typeKey)}
              onChange={() => handleExpand(typeKey)}
              sx={{
                backgroundColor: '#2D3748',
                color: 'white',
                mb: 0.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 4px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#A0AEC0' }} />}
                sx={{
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { margin: '8px 0' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Box sx={{ color: '#4299E1' }}>{getIconComponent(typeSummary.icon)}</Box>
                  <Typography sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                    {typeSummary.label}
                  </Typography>
                  <Badge
                    badgeContent={typeSummary.count}
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                  {hasMissing && (
                    <Chip
                      label={`${missingCount} Missing`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(245, 101, 101, 0.2)',
                        color: '#F56565',
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {filteredDocs.length > 0 ? (
                  <List dense sx={{ py: 0 }}>
                    {filteredDocs.map((doc, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          borderTop: '1px solid #4A5568',
                          '&:hover': { backgroundColor: '#374151' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ color: '#A0AEC0', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: 'white' }}>
                              {doc.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#A0AEC0' }}>
                              {doc.size_formatted} • {formatDate(doc.uploaded_at)}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          {doc.content_type?.includes('pdf') && (
                            <Tooltip title="Preview">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(doc)}
                                sx={{ color: '#4299E1' }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(doc)}
                              sx={{ color: '#48BB78' }}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No matching documents' : 'No documents uploaded yet'}
                    </Typography>
                    {hasMissing && !searchTerm && (
                      <Typography variant="caption" sx={{ color: '#F56565' }}>
                        Expected: {typeSummary.expected} days
                      </Typography>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* PDF Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A202C',
            height: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #4A5568',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#4299E1' }} />
            <Typography>{previewName}</Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {previewLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title={previewName}
            />
          ) : (
            <Alert severity="error">Failed to load preview</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #4A5568' }}>
          <Button onClick={handleClosePreview}>Close</Button>
          {previewUrl && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewUrl;
                link.download = previewName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocumentLibraryWidget;
